import { Box, Button, Chip, Stack, Typography } from "@mui/material";

import { ClvLabelWithHelp } from "../../../../../components/common/MetricHelp";
import { getConsentChipStyles, getCustomerStatusChipStyles } from "../../../../../lib/presentation";
import { profileValueFormatter } from "../../shared/formatters";
import { exportClientProfilePdf, exportLastOrderPdf } from "../../shared/pdfExport";
import type { OrderRow } from "../../../../../types";
import type { Client360Snapshot } from "../../../helpers/types";
import type { MasterProfile } from "../../../../../types";

interface Client360HeroCardProps {
    latestOrder: OrderRow | null;
    orders: OrderRow[];
    profile: MasterProfile;
    snapshot: Client360Snapshot;
}

export default function Client360HeroCard({
    latestOrder,
    orders,
    profile,
    snapshot,
}: Client360HeroCardProps) {
    const highlights = [
        ["Профил", profile.profileId],
        ["Магазин", profile.preferredStore || "Няма"],
        ["Канали", profile.channels || "Няма"],
        ["МОЛ", profile.mol || "Няма"],
        ["Последна поръчка", snapshot.lastOrderValue],
        ["CLV", `${profileValueFormatter.format(profile.lifetimeValue)} €`],
    ];

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 3,
                border: "1px solid rgba(210, 222, 232, 0.95)",
                background: "linear-gradient(135deg, rgba(236, 248, 244, 0.95), rgba(255, 251, 244, 0.96))",
            }}
        >
            <Stack spacing={1.15}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    sx={{ justifyContent: "space-between", alignItems: { md: "flex-start" } }}
                >
                    <Box sx={{ minWidth: 0 }}>

                        <Typography variant="h6" sx={{ mt: 0.65, lineHeight: 1.95 }}>
                            {profile.customer}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.6, lineHeight: 1.45, fontSize: "0.88rem" }}>
                            Оценка {snapshot.healthScore}/100 · {snapshot.contactReadiness} · {snapshot.orderFrequency}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        <Chip label={profile.status} size="small" sx={{ fontWeight: 700, ...getCustomerStatusChipStyles(profile.status) }} />
                        <Chip label={profile.consent} size="small" sx={{ fontWeight: 700, ...getConsentChipStyles(profile.consent) }} />
                        <Chip label={snapshot.loyaltyReadiness} size="small" sx={{ fontWeight: 700, backgroundColor: "rgba(15,23,42,0.06)" }} />
                    </Stack>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "flex-start" }}>
                    <Button variant="outlined" size="small" onClick={() => exportClientProfilePdf(profile, snapshot, orders)}>
                        PDF профил
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!latestOrder}
                        onClick={() => exportLastOrderPdf(profile, latestOrder)}
                    >
                        PDF последна поръчка
                    </Button>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" },
                        gap: 1,
                    }}
                >
                    {highlights.map(([label, value]) => (
                        <Box
                            key={label}
                            sx={{
                                p: 1.15,
                                borderRadius: 2.5,
                                border: "1px solid rgba(224, 231, 239, 0.95)",
                                backgroundColor: "rgba(255,255,255,0.78)",
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {label === "CLV" ? <ClvLabelWithHelp /> : label}
                            </Typography>
                            <Typography sx={{ mt: 0.45, fontWeight: 800, lineHeight: 1.35 }}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
}
